import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { NewsletterSectionType } from '@cineamo/api/models/newsletter-section/NewsletterSectionDto.types';

import { getNewsletterSections } from '@cineamo/api/resources/newsletter-section/newsletter-section-api';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { NewsletterTopicPreselection } from '@/providers/NewsletterModalProvider';
import { useShowingTags } from '@/providers/ShowingTagProvider';

/**
 * Subscription-topic type → newsletter section type. A topic toggle is only
 * shown while the operator's cinema has the matching section (sections are
 * add/remove only — presence means active).
 */
const SECTION_TYPE_BY_TOPIC: Record<string, NewsletterSectionType> = {
    weekly_news: 'new_releases',
    weekly_program: 'weekly_program',
    highlight: 'highlights',
    showing_tag: 'specials'
};

export type NewsletterTopicGroup = 'program' | 'highlights_specials';

export type NewsletterTopic = NewsletterTopicPreselection & {
    group: NewsletterTopicGroup;
    description: string;
};

/**
 * Shared hook for building newsletter topic lists.
 * Returns each topic with its group + description so the preferences page can
 * render grouped toggles. The modal subscribe form ignores the extra fields.
 */
export function useNewsletterTopics() {
    const { showingTags } = useShowingTags();
    const domainConfiguration = useDomainConfiguration();
    const t = useTranslations();

    const cinemaId = domainConfiguration.cinema?.id;

    // Same newsletter sections the operator manages in NewsletterConfigModal —
    // a removed section hides the matching subscriber topic.
    const { data: sectionsData } = useQuery({
        queryKey: ['newsletter-sections', cinemaId],
        queryFn: () => getNewsletterSections(cinemaId as number, { perPage: 50, sort: 'position', order: 'asc' }),
        enabled: cinemaId != null
    });

    const sections = sectionsData?._embedded?.['newsletter-sections'] ?? [];

    const allTopics = useMemo<NewsletterTopic[]>(() => {
        const presentSectionTypes = new Set(sections.map((section) => section.type));

        const topics: NewsletterTopic[] = [
            {
                type: 'weekly_news',
                label: t('newsletter-preferences.topics.weekly-news.label'),
                description: t('newsletter-preferences.topics.weekly-news.description'),
                group: 'program'
            },
            {
                type: 'weekly_program',
                label: t('newsletter-preferences.topics.weekly-program.label'),
                description: t('newsletter-preferences.topics.weekly-program.description'),
                group: 'program'
            },
            {
                type: 'highlight',
                label: t('newsletter-preferences.topics.highlight.label'),
                description: t('newsletter-preferences.topics.highlight.description'),
                group: 'highlights_specials'
            }
        ];

        if (showingTags) {
            showingTags.forEach((tag) => {
                topics.push({
                    type: 'showing_tag',
                    referenceId: String(tag.id),
                    label: tag.name,
                    description: t('newsletter-preferences.topics.showing-tag.description', { name: tag.name }),
                    group: 'highlights_specials'
                });
            });
        }

        // Hide topics whose newsletter section the operator has removed.
        // Until sections are loaded, show everything to avoid a flash.
        if (!sectionsData) {
            return topics;
        }
        return topics.filter((topic) => {
            const sectionType = SECTION_TYPE_BY_TOPIC[topic.type];
            return !sectionType || presentSectionTypes.has(sectionType);
        });
    }, [showingTags, t, sections, sectionsData]);

    const getTopicLabel = useMemo(() => {
        const labels: Record<string, string> = {};
        allTopics.forEach((topic) => {
            const key = `${topic.type}:${topic.referenceId ?? ''}`;
            labels[key] = topic.label;
        });

        return (type: string, referenceId?: string | null) => {
            const key = `${type}:${referenceId ?? ''}`;
            return labels[key] ?? type;
        };
    }, [allTopics]);

    return { allTopics, getTopicLabel };
}
